import { 
    Visibility as VisibilityIcon, 
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const ContactUs = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem("token");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleDeleteClick = async (id) => {
    try {
      if (!token) {
        console.error("Access token is missing");
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this contact!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Delete",
      });

      if (result.isConfirmed) {
        const response = await fetch(
          `https://backend.placemyfilms.com/contact/deleteContactById/${id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to delete contact: ${response.statusText}`);
        }

        setData((prevData) => prevData.filter((contact) => contact.id !== id));
        
        await Swal.fire({
          title: "Deleted!",
          text: "Contact has been deleted successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      await Swal.fire({
        title: "Error!",
        text: "Failed to delete contact. Please try again.",
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const fetchContacts = async () => {
    try {
      if (!token) {
        console.error("Access token is missing");
        return;
      }

      const response = await fetch(
        "https://backend.placemyfilms.com/contact/getAllContact",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const responseData = await response.json();

      // Check for the correct response structure based on the API response
      if (!responseData.status || !Array.isArray(responseData.contact)) {
        throw new Error("Invalid API response structure");
      }

      const mappedData = responseData.contact.map((item) => ({
        id: item.id,
        fullName: item.firstName && item.lastName 
          ? `${item.firstName} ${item.lastName}`
          : "N/A",
        phoneNumber: item.phoneNumber || "N/A",
        email: item.email || "N/A",
        message: item.message || "N/A",
        createdAt: item.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : "N/A",
      }));

      setData(mappedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Optionally show an error message to the user
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch contacts. Please try again.",
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const columns = [
    { field: "id", headerName: "Contact ID", flex: 0.5 },
    { field: "fullName", headerName: "Full Name", flex: 1 },
    { field: "phoneNumber", headerName: "Phone Number", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "message", headerName: "Message", flex: 1.5 },
    { field: "createdAt", headerName: "Created At", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <Link to={`/contactusemail/view/${params.row.id}`}>
              <IconButton>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header
        title="Contact Messages"
        subtitle="View all contact form messages in the system"
      />

      <Box
        m="0 0 0 0"
        height="65vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[800],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .row-divider": {
            borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
          },
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 20]}
          getRowClassName={() => "row-divider"}
        />
      </Box>
    </Box>
  );
};

export default ContactUs;